import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, X, Check, Question } from "@phosphor-icons/react";
import { MarketingFrame } from "../components/MarketingFrame";
import { USE_CASES, USE_CASE_LINKS, EXIF_FIELDS } from "../content/useCases";

function faqJsonLd(c, slug) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    url: `https://aiimagesuites.pro/use-cases/${slug}`,
    name: c.title,
    mainEntity: c.faqs.map(([q, a]) => ({
      "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };
}

export default function UseCasePage() {
  const { slug } = useParams();
  const c = USE_CASES[slug];
  if (!c) return <Navigate to="/" replace />;
  return (
    <MarketingFrame testid={`usecase-page-${slug}`} title={`${c.title} — AIImageSuites`} description={c.intro.slice(0, 158)} jsonLd={faqJsonLd(c, slug)}>
      <article className="px-6 py-14 max-w-5xl mx-auto w-full space-y-12">
        <header className="space-y-4">
          <p className="label-mono text-neutral-500">USE CASE · {c.audience}</p>
          <h1 className="font-display font-black tracking-tighter text-4xl sm:text-6xl leading-[0.95]" data-testid="usecase-headline">
            {c.h1[0]}<br /><span className="text-[#FF2A2A]">{c.h1[1]}</span>
          </h1>
          <p className="font-mono text-neutral-700 max-w-3xl" data-testid="usecase-intro">{c.intro}</p>
          <Link to="/signin" className="btn-flat px-5 py-3 inline-flex items-center gap-2" data-testid="usecase-cta">Start Capturing <ArrowRight size={16} weight="bold" /></Link>
        </header>

        <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="border-2 border-black p-6 bg-[#F8F9FA]" data-testid="usecase-pains">
            <p className="font-display font-bold tracking-tighter text-lg">WHAT GOES WRONG TODAY</p>
            <ul className="mt-3 space-y-2 text-sm font-mono text-neutral-700">
              {c.pains.map((p, i) => <li key={i} className="flex gap-2"><X size={16} weight="bold" className="text-[#FF2A2A] shrink-0 mt-0.5" /><span>{p}</span></li>)}
            </ul>
          </div>
          <div className="border-2 border-black p-6" data-testid="usecase-exif">
            <p className="font-display font-bold tracking-tighter text-lg">WHAT GETS BAKED INTO EVERY FILE</p>
            <ul className="mt-3 space-y-2 text-sm font-mono text-neutral-700">
              {EXIF_FIELDS.map((f, i) => <li key={i} className="flex gap-2"><Check size={16} weight="bold" className="text-green-700 shrink-0 mt-0.5" /><span>{f}</span></li>)}
            </ul>
          </div>
        </section>

        <section data-testid="usecase-steps">
          <p className="label-mono text-neutral-500 mb-4">THE WORKFLOW</p>
          <ol className="grid grid-cols-1 md:grid-cols-4 border-2 border-black divide-y-2 md:divide-y-0 md:divide-x-2 divide-black">
            {c.steps.map(([t, d], i) => (
              <li key={i} className="p-5">
                <span className="font-display font-black text-3xl tracking-tighter text-neutral-300">{String(i + 1).padStart(2, "0")}</span>
                <h2 className="font-display font-bold tracking-tighter text-base mt-2">{t}</h2>
                <p className="text-xs font-mono text-neutral-700 mt-2 leading-relaxed">{d}</p>
              </li>
            ))}
          </ol>
        </section>

        <section data-testid="usecase-faq">
          <p className="label-mono text-neutral-500 mb-4">QUESTIONS PEOPLE ASK</p>
          <div className="border-2 border-black divide-y-2 divide-black">
            {c.faqs.map(([q, a], i) => (
              <details key={i} className="group p-5" data-testid={`usecase-faq-${i}`} open={i === 0}>
                <summary className="cursor-pointer list-none flex items-start gap-2 font-display font-bold tracking-tighter">
                  <Question size={18} weight="bold" className="shrink-0 mt-0.5" />{q}
                </summary>
                <p className="text-sm font-mono text-neutral-700 mt-3 pl-7">{a}</p>
              </details>
            ))}
          </div>
        </section>

        <footer className="border-t-2 border-black pt-8 flex flex-col sm:flex-row gap-6 justify-between">
          <div className="label-mono text-neutral-600 flex flex-wrap gap-4">
            {c.related.map(([to, l]) => <Link key={to} to={to} className="underline underline-offset-4 hover:text-black">{l}</Link>)}
          </div>
          <div className="label-mono text-neutral-500 flex flex-wrap gap-4" data-testid="usecase-others">
            {USE_CASE_LINKS.filter((l) => !l.to.endsWith(slug)).map((l) => <Link key={l.to} to={l.to} className="hover:text-black">{l.label} →</Link>)}
          </div>
        </footer>
      </article>
    </MarketingFrame>
  );
}
